import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface CoachPreferences {
  showDemoContent: boolean;
}

const DEFAULT_PREFERENCES: CoachPreferences = {
  showDemoContent: true,
};

export const useCoachPreferences = () => {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['coach-preferences', userId],
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    initialData: DEFAULT_PREFERENCES,
    queryFn: async () => {
      if (!userId) {
        return DEFAULT_PREFERENCES;
      }

      const { data, error } = await supabase
        .from('coach_settings')
        .select('show_demo_content')
        .eq('user_id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return {
        showDemoContent: data?.show_demo_content ?? true,
      };
    },
  });

  const updatePreferences = useMutation({
    mutationFn: async (updates: Partial<CoachPreferences>) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const payload: Record<string, unknown> = {
        user_id: userId,
      };

      if (updates.showDemoContent !== undefined) {
        payload.show_demo_content = updates.showDemoContent;
      }

      const { error } = await supabase
        .from('coach_settings')
        .upsert(payload, { onConflict: 'user_id' });

      if (error) {
        throw error;
      }

      return updates;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coach-preferences', userId] });
    },
  });

  return {
    preferences: query.data ?? DEFAULT_PREFERENCES,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    updatePreferences: updatePreferences.mutateAsync,
    updating: updatePreferences.isPending,
  };
};
